/* __next_internal_client_entry_do_not_use__ WithSearchPageUseQueryContextProvider,SearchPageBannersUseQueryDataProvider auto */ import { jsx as _jsx } from "react/jsx-runtime";
import { useSearchPageBannersResponse } from "@faire/retailer-visitor-shared/components/Banners/DataFetching/useSearchPageBannersData";
import { useRawBannerData } from "@faire/retailer-visitor-shared/components/Banners/SearchBanners/RawBannerDataContextProvider";
import { DiscoverPageBannersUseQueryDataProvider } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/ViewState/DiscoverPageBannersUseQueryDataProvider";
import { SearchPageBannersDataContextProvider, useSearchPageBannersDataContext } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/ViewState/SearchPageBannersDataContext";
import { SearchPageDataContextProvider } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/ViewState/SearchPageDataContext";
import { getPageSize, getShouldWaitForBannersQuery } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/ViewState/searchPageDataUtil";
import { SearchPageFiltersDataContextProvider } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/ViewState/SearchPageFiltersDataContext";
import { useSearchPageFiltersQueryArgs, useSearchPageQueryArgs } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/useSearchPageQueryArgs";
import { useSearchPageFilters } from "@faire/retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageFilters";
import { useSearchPageIsFromDiscoverURL } from "@faire/retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageIsFromDiscoverURL";
import { useSearchPageProductSearchString } from "@faire/retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageProductSearchString";
import { useSearchPageZeroIndexedPageNumber } from "@faire/retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageZeroIndexedPageNumber";
import fetchSearchPageBanners from "@faire/web-api--source/api/v2/products/search/banners/post";
import { ISearchCatNavBannersRequest } from "@faire/web-api--source/indigofair/data/ISearchCatNavBannersRequest";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import React, { useMemo } from "react";
export var WithSearchPageUseQueryContextProvider = function(param) {
    var children = param.children;
    return /*#__PURE__*/ _jsx(BannersUseQueryDataProvider, {
        children: /*#__PURE__*/ _jsx(SearchPageProductsUseQueryDataProvider, {
            children: /*#__PURE__*/ _jsx(SearchPageFiltersUseQueryDataProvider, {
                children: children
            })
        })
    });
};
var BannersUseQueryDataProvider = function(param) {
    var children = param.children;
    var isFromDiscoverURL = useSearchPageIsFromDiscoverURL();
    if (isFromDiscoverURL) {
        return /*#__PURE__*/ _jsx(DiscoverPageBannersUseQueryDataProvider, {
            children: children
        });
    }
    return /*#__PURE__*/ _jsx(SearchPageBannersUseQueryDataProvider, {
        children: children
    });
};
export var SearchPageBannersUseQueryDataProvider = function(param) {
    var children = param.children;
    var query = useSearchPageProductSearchString();
    var filters = useSearchPageFilters();
    /**
     * In RSC, we fetch the banners data at the server and send it to the client
     * side via RawBannerDataContext. If the data is available, we use it directly
     * instead of fetching it again.
     */ var serverData = useRawBannerData();
    var request = ISearchCatNavBannersRequest.build({
        query: query,
        filter_keys: filters
    });
    // if the serverData is not available, we fetch it again
    var _useQuery = useQuery({
        queryKey: [
            request
        ],
        queryFn: function() {
            return fetchSearchPageBanners(request);
        },
        enabled: !!query && !serverData,
        staleTime: Infinity,
        placeholderData: keepPreviousData
    }), clientData = _useQuery.data;
    var _useSearchPageBannersResponse = useSearchPageBannersResponse(serverData !== null && serverData !== void 0 ? serverData : clientData), bannersResponse = _useSearchPageBannersResponse.bannersResponse, bannersDisplayData = _useSearchPageBannersResponse.bannersDisplayData, replacedProductTilesCount = _useSearchPageBannersResponse.replacedProductTilesCount, displayableBannersWithAdjustedRow = _useSearchPageBannersResponse.displayableBannersWithAdjustedRow;
    var data = useMemo(function() {
        return {
            bannersResponse: bannersResponse,
            bannersDisplayData: bannersDisplayData,
            replacedProductTilesCount: replacedProductTilesCount,
            displayableBannersWithAdjustedRow: displayableBannersWithAdjustedRow
        };
    }, [
        bannersResponse,
        bannersDisplayData,
        replacedProductTilesCount,
        displayableBannersWithAdjustedRow
    ]);
    return /*#__PURE__*/ _jsx(SearchPageBannersDataContextProvider, {
        searchBannersData: data,
        children: children
    });
};
var SearchPageProductsUseQueryDataProvider = function(param) {
    var children = param.children;
    var searchBannersData = useSearchPageBannersDataContext();
    var pageSize = getPageSize(searchBannersData.replacedProductTilesCount);
    var zeroIndexedPageNumber = useSearchPageZeroIndexedPageNumber();
    var shouldWaitForBannersQuery = getShouldWaitForBannersQuery(zeroIndexedPageNumber, !searchBannersData.bannersResponse);
    var productsQueryArgs = useSearchPageQueryArgs(pageSize).productsQueryArgs;
    var productsQuery = useQuery({
        queryKey: productsQueryArgs.key,
        queryFn: productsQueryArgs.fn,
        enabled: !shouldWaitForBannersQuery,
        staleTime: productsQueryArgs.staleTime
    });
    var data = useMemo(function() {
        return {
            productsData: {
                legacyResponse: productsQuery.data,
                legacyProductTokens: undefined
            },
            loading: productsQuery.isFetching,
            isError: productsQuery.isError,
            error: productsQuery.error,
            dataUpdatedAt: productsQuery.dataUpdatedAt,
            rscPageLoadKey: ""
        };
    }, [
        productsQuery.data,
        productsQuery.dataUpdatedAt,
        productsQuery.error,
        productsQuery.isError,
        productsQuery.isFetching
    ]);
    return /*#__PURE__*/ _jsx(SearchPageDataContextProvider, {
        searchPageData: data,
        children: children
    });
};
var SearchPageFiltersUseQueryDataProvider = function(param) {
    var children = param.children;
    var filtersQueryArgs = useSearchPageFiltersQueryArgs().filtersQueryArgs;
    var filtersQuery = useQuery({
        queryKey: filtersQueryArgs.key,
        queryFn: filtersQueryArgs.fn,
        staleTime: filtersQueryArgs.staleTime
    });
    var data = useMemo(function() {
        return {
            legacyFiltersResponse: filtersQuery.data,
            loading: filtersQuery.isFetching,
            isError: filtersQuery.isError,
            error: filtersQuery.error
        };
    }, [
        filtersQuery.data,
        filtersQuery.error,
        filtersQuery.isError,
        filtersQuery.isFetching
    ]);
    return /*#__PURE__*/ _jsx(SearchPageFiltersDataContextProvider, {
        searchPageFiltersData: data,
        children: children
    });
};
